import { useEffect, useMemo }      from 'react'
import { useAppStore }             from '@/tools/exmarked/stores/appStore'
import { getProvider }             from '@/tools/exmarked/lib/providers'
import { renderMarkdown }          from '@/tools/exmarked/lib/markdown'
import { parseTOC }                from '@/tools/exmarked/lib/toc'
import { mdComponents }            from './mdComponents'
import { TableOfContents }         from './TableOfContents'

// ─── Loading Skeleton ─────────────────────────────────────────────────────────

function LoadingSkeleton() {
  return (
    <div className="max-w-4xl mx-auto px-8 py-12 animate-pulse">
      <div className="h-9 bg-forge-basalt rounded w-3/5 mb-6" />
      <div className="space-y-3 mb-8">
        <div className="h-4 bg-forge-basalt rounded w-full" />
        <div className="h-4 bg-forge-basalt rounded w-5/6" />
        <div className="h-4 bg-forge-basalt rounded w-4/6" />
      </div>
      <div className="h-7 bg-forge-basalt rounded w-2/5 mb-4" />
      <div className="space-y-3 mb-8">
        <div className="h-4 bg-forge-basalt rounded w-full" />
        <div className="h-4 bg-forge-basalt rounded w-11/12" />
        <div className="h-4 bg-forge-basalt rounded w-4/5" />
        <div className="h-4 bg-forge-basalt rounded w-3/5" />
      </div>
      <div className="h-32 bg-forge-obsidian rounded-lg border border-forge-border mb-6" />
      <div className="space-y-3">
        <div className="h-4 bg-forge-basalt rounded w-full" />
        <div className="h-4 bg-forge-basalt rounded w-3/4" />
      </div>
    </div>
  )
}

// ─── Empty State ──────────────────────────────────────────────────────────────

function EmptyState() {
  return (
    <div className="flex items-center justify-center h-full text-center p-8">
      <div className="space-y-3">
        <div className="w-12 h-12 rounded-lg border border-forge-border flex items-center justify-center mx-auto">
          <svg className="w-6 h-6 text-forge-ash" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
        </div>
        <p className="text-forge-ash text-sm">No document loaded</p>
      </div>
    </div>
  )
}

// ─── Error State ──────────────────────────────────────────────────────────────

function ErrorState({ message }: { message: string }) {
  const { backToWorkspace } = useAppStore()
  return (
    <div className="max-w-4xl mx-auto px-8 py-12">
      <div className="bg-forge-heat-critical/10 border border-forge-heat-critical/30 rounded-lg p-6">
        <p className="text-forge-heat-critical font-semibold mb-2">Failed to load document</p>
        <p className="text-forge-ash text-sm font-mono leading-relaxed mb-5">{message}</p>
        <button
          onClick={backToWorkspace}
          className="text-xs text-forge-ash hover:text-forge-primary border border-forge-border rounded px-3 py-1.5 transition-colors"
        >
          ← Back to file list
        </button>
      </div>
    </div>
  )
}

// ─── Viewer ───────────────────────────────────────────────────────────────────

export function MarkdownViewer() {
  const {
    selectedSource,
    selectedFilePath,
    selectedFileId,
    fileTree,
    markdownContent,
    isLoadingFile,
    error,
    setMarkdownContent,
    setError,
  } = useAppStore()

  // Fetch file content whenever the selected file changes
  useEffect(() => {
    if (!selectedSource || !selectedFilePath || !selectedFileId) return
    let cancelled = false

    // Reconstruct the FileNode so the provider can use its id
    const file = fileTree.find((f) => f.path === selectedFilePath) ?? {
      name: selectedFilePath.split('/').pop() ?? selectedFilePath,
      path: selectedFilePath,
      type: 'file' as const,
      id:   selectedFileId,
    }

    getProvider(selectedSource)
      .readFile(selectedSource, file)
      .then((content) => { if (!cancelled) setMarkdownContent(content) })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Unknown error loading file.')
          setMarkdownContent(null)
        }
      })

    return () => { cancelled = true }
  }, [selectedFileId, selectedSource?.id, selectedFilePath, fileTree, setMarkdownContent, setError])

  const rendered = useMemo(() => {
    if (!markdownContent) return null
    try {
      return renderMarkdown(markdownContent, mdComponents)
    } catch (err) {
      return { __renderError: err instanceof Error ? err.message : String(err) }
    }
  }, [markdownContent])

  const toc = useMemo(() => {
    if (!markdownContent) return []
    return parseTOC(markdownContent)
  }, [markdownContent])

  if (isLoadingFile) return <LoadingSkeleton />

  if (error && error !== '__truncated__') return <ErrorState message={error} />

  if (!markdownContent || rendered === null) return <EmptyState />

  if (rendered && '__renderError' in rendered) {
    return <ErrorState message={(rendered as { __renderError: string }).__renderError} />
  }

  return (
    <div data-scroll-container className="h-full overflow-auto">
      <div className="max-w-6xl mx-auto flex gap-8 px-8">

        <article
          id="exmarked-document"
          className="flex-1 min-w-0 py-12 animate-fade-in"
        >
          {rendered}
        </article>

        {toc.length > 0 && (
          <aside className="hidden xl:block w-52 flex-shrink-0 py-12">
            <div className="sticky top-8">
              <TableOfContents entries={toc} />
            </div>
          </aside>
        )}

      </div>
    </div>
  )
}
